import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Icon } from '@/components/ui/icon';
import { Text } from '@/components/ui/text';
import { useAuth } from '@/lib/auth/auth';
import { useAppearance } from '@/lib/design/appearance';
import { triggerConfirmHaptic, triggerSelectionHaptic } from '@/lib/platform/haptics';
import { formatBadgeCountLabel } from '@/lib/notifications/notifications';
import { getProfileInitials } from '@/features/profile/profileFormatting';
import { useAccountContext } from './useAccountContext';
import { useAccountContextSheetStore } from './useAccountContextSheetStore';
import { useBusinessNotificationUnreadCounts } from '@/features/pulse/useNotifications';
import { DESIGN_TOKENS } from '@/lib/design/theme';
import { trackWorkspaceContextSwitched } from '@/lib/analytics/posthog';
import { showToast } from '@/lib/useToastStore';
import { getVenueCategoryConfig } from '@/features/venues/venueCategories';
import { useVenueSwitcherDetails } from './useVenueSwitcherDetails';
import {
  formatWorkspaceRole,
  getBusinessShellHref,
} from './workspace';
import {
  BottomSheetBackdrop,
  BottomSheetModal,
  BottomSheetScrollView,
} from '@gorhom/bottom-sheet';
import type { BottomSheetDefaultBackdropProps } from '@gorhom/bottom-sheet/lib/typescript/components/bottomSheetBackdrop/types';
import { router } from 'expo-router';
import { CheckIcon } from 'lucide-react-native';
import * as React from 'react';
import { Platform, Pressable, View, useWindowDimensions } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { FullWindowOverlay } from 'react-native-screens';

export function AccountContextSheet() {
  const insets = useSafeAreaInsets();
  const { height } = useWindowDimensions();
  const { isOpen, hide } = useAccountContextSheetStore();
  const { currentContext, availableContexts, setBusinessContext, setPersonalContext } =
    useAccountContext();
  const bottomSheetRef = React.useRef<BottomSheetModal>(null);
  const maxDynamicContentSize = React.useMemo(
    () => Math.round(height * 0.8),
    [height]
  );
  const personalDisplayName = useAuth((state) => state.session?.me.profile.display_name);
  const personalUsername = useAuth((state) => state.session?.me.profile.username);
  const personalAvatarURL = useAuth((state) => state.session?.me.profile.avatar_url) ?? '';
  const personalName = personalDisplayName ?? personalUsername ?? 'You';
  const resolvedMode = useAppearance((state) => state.resolvedMode);
  const isDay = resolvedMode === 'day';

  const renderBackdrop = React.useCallback(
    (props: BottomSheetDefaultBackdropProps) => (
      <BottomSheetBackdrop {...props} disappearsOnIndex={-1} appearsOnIndex={0} opacity={0.52} />
    ),
    []
  );

  React.useEffect(() => {
    if (isOpen) {
      void triggerSelectionHaptic();
      requestAnimationFrame(() => bottomSheetRef.current?.present());
      return;
    }
    bottomSheetRef.current?.dismiss();
  }, [isOpen]);
  const venueGroups = React.useMemo(
    () =>
      availableContexts.flatMap((ctx) =>
        ctx.kind === 'business'
          ? [{
              orgId: ctx.membership.organization_id,
              orgName: ctx.membership.organization_name,
              role: ctx.membership.role,
              venues: ctx.membership.venues,
            }]
          : []
      ),
    [availableContexts]
  );
  const venueIDs = React.useMemo(
    () => venueGroups.flatMap((group) => group.venues.map((venue) => venue.venue_id)),
    [venueGroups]
  );
  const venueDetails = useVenueSwitcherDetails(venueIDs);
  const unreadCountsByOrg = useBusinessNotificationUnreadCounts(venueGroups.map((group) => group.orgId));
  const isPersonalActive = currentContext?.kind === 'personal';
  const activeVenueName =
    currentContext?.kind === 'business'
      ? venueGroups
          .find((group) => group.orgId === currentContext.activeOrgId)
          ?.venues.find((venue) => venue.venue_id === currentContext.activeVenueId)?.venue_name
      : null;
  const optionCount = 1 + venueIDs.length;
  const useScrollableSheet = optionCount > 5;
  const sheetBackgroundColor = isDay
    ? DESIGN_TOKENS.colors.surface
    : DESIGN_TOKENS.colors.surfaceOverlay;

  if (availableContexts.length < 2) return null;

  const contentContainerStyle = {
    paddingHorizontal: 16,
    paddingTop: 8,
    paddingBottom: insets.bottom + 22,
    gap: 10,
  } as const;

  const content = (
    <>
      <Pressable
        className="overflow-hidden rounded-2xl border"
        style={{
          backgroundColor: isPersonalActive
            ? isDay
              ? DESIGN_TOKENS.colors.surfaceAlt
              : DESIGN_TOKENS.colors.surfaceRaised
            : isDay
              ? DESIGN_TOKENS.colors.surface
              : DESIGN_TOKENS.colors.surfaceOverlay,
          borderColor: isPersonalActive
            ? DESIGN_TOKENS.colors.accentEdge
            : isDay
              ? DESIGN_TOKENS.colors.border
              : DESIGN_TOKENS.colors.border,
        }}
        onPress={() => {
          trackWorkspaceContextSwitched({ targetContext: 'personal' });
          void setPersonalContext().then(() => {
            void triggerConfirmHaptic();
            hide();
            router.replace('/(tabs)/events' as never);
          });
        }}>
        <View className="flex-row items-center gap-3 px-4 py-3">
          <Avatar className="size-9 border border-border bg-muted">
            {personalAvatarURL.trim().length > 0 ? (
              <AvatarImage source={{ uri: personalAvatarURL }} />
            ) : null}
            <AvatarFallback>
              <Text variant="small" className="font-semibold text-foreground">
                {getProfileInitials(personalName)}
              </Text>
            </AvatarFallback>
          </Avatar>
          <View className="flex-1">
            <Text className="font-semibold text-foreground">{personalName}</Text>
            <Text variant="small" className="text-muted-foreground">
              Events, map, and your profile
            </Text>
          </View>
          <SelectionIndicator active={isPersonalActive} />
        </View>
      </Pressable>

      {venueGroups.map((group) => (
        <View key={group.orgId} className="gap-2">
          <View className="flex-row items-center justify-between gap-3 px-2 pt-1">
            <Text
              variant="small"
              className="flex-1 font-medium uppercase tracking-[1.1px] text-muted-foreground/75">
              {group.orgName}{' '}
              <Text variant="small" className="text-muted-foreground/55">
                · {formatWorkspaceRole(group.role)}
              </Text>
            </Text>
            {formatBadgeCountLabel(unreadCountsByOrg[group.orgId] ?? 0) ? (
              <View
                className="rounded-full px-2.5 py-1"
                style={{
                  backgroundColor: isDay
                    ? DESIGN_TOKENS.colors.accentSoft
                    : DESIGN_TOKENS.colors.accentPressed,
                }}>
                <Text
                  variant="small"
                  className="text-[11px] font-semibold tracking-[0.04em]"
                  style={{
                    color: isDay ? DESIGN_TOKENS.colors.accentPressed : DESIGN_TOKENS.colors.canvas,
                  }}>
                  {formatBadgeCountLabel(unreadCountsByOrg[group.orgId] ?? 0)}
                </Text>
              </View>
            ) : null}
          </View>

          {group.venues.length > 0 ? (
            group.venues.map((venue) => {
              const isActive =
                currentContext?.kind === 'business' &&
                currentContext.activeOrgId === group.orgId &&
                currentContext.activeVenueId === venue.venue_id;
              return (
                <Pressable
                  key={venue.venue_id}
                  className="overflow-hidden rounded-2xl border"
                  style={{
                    backgroundColor: isActive
                      ? isDay
                        ? DESIGN_TOKENS.colors.surfaceAlt
                        : DESIGN_TOKENS.colors.surfaceRaised
                      : isDay
                        ? DESIGN_TOKENS.colors.surface
                        : DESIGN_TOKENS.colors.surfaceOverlay,
                    borderColor: isActive
                      ? DESIGN_TOKENS.colors.accentEdge
                      : DESIGN_TOKENS.colors.border,
                  }}
                  onPress={() => {
                    trackWorkspaceContextSwitched({
                      organizationID: group.orgId,
                      targetContext: 'business',
                      venueID: venue.venue_id,
                    });
                    void setBusinessContext(group.orgId, venue.venue_id).then(() => {
                      void triggerConfirmHaptic();
                      showToast({ message: `Viewing ${venue.venue_name}.` });
                      hide();
                      router.replace(getBusinessShellHref('home') as never);
                    });
                  }}>
                  <View className="flex-row items-center gap-3 px-4 py-3">
                    <View
                      className="size-9 items-center justify-center rounded-xl"
                      style={{
                        backgroundColor: isDay
                          ? DESIGN_TOKENS.colors.surfaceRaised
                          : DESIGN_TOKENS.colors.canvasInset,
                      }}>
                      <Icon
                        as={getVenueCategoryConfig(venueDetails.get(venue.venue_id)?.category).icon}
                        size={15}
                        className="text-muted-foreground"
                      />
                    </View>
                    <View className="flex-1 gap-0.5">
                      <Text className="font-medium text-foreground">{venue.venue_name}</Text>
                      {isActive ? (
                        <Text variant="small" className="font-semibold text-primary">
                          Active venue
                        </Text>
                      ) : null}
                    </View>
                    <SelectionIndicator active={isActive} />
                  </View>
                </Pressable>
              );
            })
          ) : (
            <View className="rounded-md border border-dashed border-border p-3">
              <Text variant="small" className="text-muted-foreground">
                No venues assigned to this org yet
              </Text>
            </View>
          )}
        </View>
      ))}
    </>
  );

  return (
    <BottomSheetModal
      ref={bottomSheetRef}
      index={0}
      enableDynamicSizing
      maxDynamicContentSize={maxDynamicContentSize}
      animateOnMount
      enablePanDownToClose
      onDismiss={hide}
      bottomInset={0}
      backdropComponent={renderBackdrop}
      containerComponent={Platform.OS === 'ios' ? (FullWindowOverlay as any) : undefined}
      backgroundStyle={{
        backgroundColor: sheetBackgroundColor,
        borderTopLeftRadius: 26,
        borderTopRightRadius: 26,
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        borderWidth: 1,
        borderColor: isDay ? DESIGN_TOKENS.colors.border : DESIGN_TOKENS.colors.border,
      }}
      handleIndicatorStyle={{
        backgroundColor: isDay
          ? DESIGN_TOKENS.colors.borderDock
          : DESIGN_TOKENS.colors.borderStrong,
        width: 42,
        height: 5,
      }}
      style={{
        marginHorizontal: 0,
        shadowColor: isDay ? '#7f6323' : '#000000',
        shadowOpacity: isDay ? 0.12 : 0.24,
        shadowRadius: isDay ? 20 : 16,
        shadowOffset: { width: 0, height: isDay ? 10 : 6 },
      }}>
      <View
        style={{
          backgroundColor: sheetBackgroundColor,
          borderTopLeftRadius: 26,
          borderTopRightRadius: 26,
          overflow: 'hidden',
        }}>
        <View className="border-b border-border px-4 pb-3">
          <Text
            variant="small"
            className="text-[11px] font-medium uppercase tracking-[1.2px] text-muted-foreground/70">
            View as
          </Text>
          {activeVenueName ? (
            <Text className="mt-1 text-base font-semibold text-foreground" numberOfLines={1}>
              {activeVenueName}
            </Text>
          ) : null}
        </View>
        <BottomSheetScrollView
          style={{ backgroundColor: sheetBackgroundColor }}
          contentContainerStyle={contentContainerStyle}
          showsVerticalScrollIndicator={useScrollableSheet}
          scrollEnabled={useScrollableSheet}>
          {content}
        </BottomSheetScrollView>
      </View>
    </BottomSheetModal>
  );
}

function SelectionIndicator({ active }: { active: boolean }) {
  if (active) {
    return (
      <View
        className="size-7 items-center justify-center rounded-full border"
        style={{
          backgroundColor: DESIGN_TOKENS.colors.accentSoft,
          borderColor: DESIGN_TOKENS.colors.accentEdge,
        }}>
        <Icon as={CheckIcon} size={13} color={DESIGN_TOKENS.colors.accentPressed} />
      </View>
    );
  }

  return <View className="size-6 rounded-full border" style={{ borderColor: DESIGN_TOKENS.colors.border }} />;
}
